import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';

type ChatOpenState = 'open' | 'closed' | 'hidden';

interface ChatBoxState {
    id: number;
    isOpen: boolean;
}

export const DRAWER_WIDTH_REM = 28;
export const CHAT_BOX_OPEN_WIDTH_PX = 480;
export const CHAT_BOX_CLOSED_WIDTH_PX = 240;
export const SPACING_PX = 16;

/**
 * The chat box(es) open state.
 * - open: The chat messages drawer is open.
 * - closed: The chat messages drawer is closed but the button is visible.
 * - hidden: The chat messages drawer button is hidden.
 */
const chatOpenStateAtom = atom<ChatOpenState>('closed');
/**
 * The list of open chat conversations IDs and their open state. This is ordered
 * by the order in which the chat boxes were opened.
 */
const chatConversationsListAtom = atom<ChatBoxState[]>([]);
/**
 * The pending message to be sent when the user sends a message in a new message
 * chat box. When the chat box with this conversation is opened, the message will
 * be sent automatically.
 */
const pendingMessageAtom = atom<{
    conversationID: number;
    message: string;
    attachments: File[];
} | null>(null);

/**
 * Hook to handle the website chat box functionality. This includes the chat box(es)
 * open state, and the list of open chat conversations.
 */
export function useChat() {
    const [chatOpenState, setChatOpenState] = useAtom(chatOpenStateAtom);
    const [chatConversationsList, setChatConversationsList] = useAtom(
        chatConversationsListAtom
    );
    const [pendingMessage, setPendingMessage] = useAtom(pendingMessageAtom);

    const isChatBoxOpen = useCallback(
        (conversationID: number): boolean => {
            return chatConversationsList.some(
                (c) => c.id === conversationID && c.isOpen
            );
        },
        [chatConversationsList]
    );

    const openChatBox = useCallback(
        (conversationID: number) => {
            setChatConversationsList((prev) => {
                const currentState = prev.find((c) => c.id === conversationID);
                // If the chat box is already open, do nothing.
                if (currentState?.isOpen) {
                    return prev;
                }
                const newState = minimiseOrCloseOtherChatBoxes(
                    prev,
                    conversationID,
                    !!currentState
                );
                // If the chat box is minimised, open it.
                if (currentState) {
                    return newState.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: true } : c
                    );
                }
                // If the chat box is not open, open it.
                return [...newState, { id: conversationID, isOpen: true }];
            });
        },
        [setChatConversationsList]
    );

    const convertNewMessageToConversation = useCallback(
        (conversationID: number) => {
            if (conversationID <= 0) return;
            setChatConversationsList((prev) => {
                return prev
                    .filter((c) => c.id !== conversationID)
                    .map((c) =>
                        c.id === 0 ? { id: conversationID, isOpen: true } : c
                    );
            });
        },
        [setChatConversationsList]
    );

    return {
        /**
         * The chat box open state.
         */
        chatOpenState,
        /**
         * The list of open chat conversations IDs.
         */
        chatConversationsList,
        /**
         * The pending message to be sent when the chat box with this
         * conversation is opened. This is set when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        pendingMessage,
        /**
         * Set the chat box open state.
         */
        setChatOpenState,
        /**
         * Check if the chat box for a conversation is open.
         */
        isChatBoxOpen,
        /**
         * Open the chat box for a conversation. If there is no available screen
         * space, the oldest chat box(es) will be minimised or closed. Pass `0`
         * as the conversation ID to create a new conversation.
         */
        openChatBox,
        /**
         * Minimize the chat box for a conversation.
         */
        minimizeChatBox: useCallback(
            (conversationID: number) => {
                setChatConversationsList((prev) => {
                    return prev.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: false } : c
                    );
                });
            },
            [setChatConversationsList]
        ),
        /**
         * Close the chat box for a conversation.
         */
        closeChatBox: useCallback(
            (conversationID: number) => {
                setChatConversationsList((prev) => {
                    return prev.filter((c) => c.id !== conversationID);
                });
            },
            [setChatConversationsList]
        ),
        /**
         * Set the pending message to be sent when the chat box with this
         * conversation is opened. Call this when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        setPendingMessage,
        /**
         * Convert the new message chat box to a conversation chat box. If the
         * new message chat box is not open, this will do nothing except close
         * the conversation chat box it exists.
         */
        convertNewMessageToConversation,
    };
}

/**
 * Minimise or close other chat boxes to make space for the new chat box to open.
 * @param chatBoxes The list of chat boxes.
 * @param conversationID The ID of the conversation to be opened.
 * @param isMinimised The chat box to open is currently minimised, instead of closed.
 * @returns The list of chat boxes after minimising or closing other chat boxes.
 */
function minimiseOrCloseOtherChatBoxes(
    chatBoxes: ChatBoxState[],
    conversationID: number,
    isMinimised: boolean
): ChatBoxState[] {
    const requiredSpace = isMinimised
        ? CHAT_BOX_OPEN_WIDTH_PX - CHAT_BOX_CLOSED_WIDTH_PX
        : CHAT_BOX_OPEN_WIDTH_PX + SPACING_PX;

    let newState = [...chatBoxes];
    // First, minimise open chat boxes one by one until there's enough space.
    let availableSpace = calculateAvailableSpace(newState);
    while (availableSpace < requiredSpace) {
        const indexToMinimise = newState.findIndex(
            (c) => c.isOpen && c.id !== conversationID
        );
        if (indexToMinimise === -1) {
            // No more open chat boxes to minimise.
            break;
        }
        // Minimise this chat box.
        newState[indexToMinimise] = {
            ...newState[indexToMinimise]!,
            isOpen: false,
        };
        availableSpace = calculateAvailableSpace(newState);
    }

    // If there's still not enough space, close chat boxes one by one.
    while (availableSpace < requiredSpace) {
        const indexToClose = newState.findIndex((c) => c.id !== conversationID);
        if (indexToClose === -1) {
            // No more chat boxes to close.
            break;
        }
        // Close this chat box.
        newState.splice(indexToClose, 1);
        availableSpace = calculateAvailableSpace(newState);
    }

    return newState;
}

/**
 * Calculate the available space for a new chat box. This is calculated by
 * taking the window width and subtracting the width of the other chat boxes.
 * @param chatBoxes The list of chat boxes.
 * @returns The available space for new chat boxes in pixels. The value is
 *  clamped between 0 and 1920.
 */
function calculateAvailableSpace(chatBoxes: ChatBoxState[]): number {
    const windowWidth = window.visualViewport?.width || window.innerWidth;
    const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
    );
    const drawerWidthPx = DRAWER_WIDTH_REM * rootFontSize;

    const chatBoxesWidthPx = chatBoxes.reduce((acc, chatBox) => {
        return (
            acc +
            (chatBox.isOpen
                ? CHAT_BOX_OPEN_WIDTH_PX
                : CHAT_BOX_CLOSED_WIDTH_PX) +
            SPACING_PX
        );
    }, 0);

    return Math.min(
        Math.max(windowWidth - drawerWidthPx - chatBoxesWidthPx, 0),
        1920
    );
}
